import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getDashboard } from "../../services/adminApi";

const Dashboard = () => {

    const [dashboard, setDashboard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {

        const loadDashboard = async () => {

            try {

                setLoading(true);
                setError("");

                const result = await getDashboard();

                if (result.success) {
                    setDashboard(result.data);
                }

            } catch (err) {

                console.error(err);

                setError(
                    err.message ||
                    "Unable to load dashboard."
                );

            } finally {

                setLoading(false);

            }
        };

        loadDashboard();

    }, []);


    if (loading) {

        return (
            <div className="admin-dashboard-loading">
                <div className="admin-spinner" />
                <p>Loading dashboard...</p>
            </div>
        );

    }


    if (error) {

        return (
            <div className="admin-dashboard-error">

                <h3>
                    Unable to load dashboard
                </h3>

                <p>
                    {error}
                </p>

            </div>
        );

    }


    const statistics = [

        {
            label: "Projects",
            value: dashboard?.statistics?.projects?.total ?? 0,
            change:
                `+${dashboard?.statistics?.projects?.this_month ?? 0} this month`,
            icon: "▧",
            path: "/admin/projects",
        },

        {
            label: "News Articles",
            value: dashboard?.statistics?.news?.total ?? 0,
            change:
                `+${dashboard?.statistics?.news?.this_month ?? 0} this month`,
            icon: "▥",
            path: "/admin/news",
        },

        {
            label: "Media Files",
            value: dashboard?.statistics?.media?.total ?? 0,
            change:
                `+${dashboard?.statistics?.media?.this_month ?? 0} this month`,
            icon: "▣",
            path: "/admin/media",
        },

    ];


    const activities =
        dashboard?.activities || [];


    const administrator =
        dashboard?.administrator;


    const status =
        dashboard?.status;


    const getGreeting = () => {
        const hour = new Date().getHours();

        if (hour < 12) {
            return "Good morning";
        }

        if (hour < 17) {
            return "Good afternoon";
        }

        return "Good evening";
    };


    return (

        <div className="admin-dashboard">

            {/* =========================================
                PAGE HEADER
            ========================================= */}

            <div className="admin-page-header">

                <div>

                    <span className="admin-page-eyebrow">
                        OVERVIEW
                    </span>

                    <h2>

                    {getGreeting()},
                        <em>
                            {" "}
                            {administrator?.name || "Administrator"}.
                        </em>

                    </h2>

                    <p>
                        Here's what's happening across
                        your website today.
                    </p>

                </div>


                <Link
                    to="/admin/projects"
                    className="admin-primary-button"
                >

                    <span>
                        +
                    </span>

                    Add New Project

                </Link>

            </div>


            {/* =========================================
                STATISTICS
            ========================================= */}

            <div className="admin-stat-grid">

                {statistics.map((stat) => (

                    <Link
                        to={stat.path}
                        className="admin-stat-card"
                        key={stat.label}
                    >

                        <div className="admin-stat-top">

                            <div className="admin-stat-icon">
                                {stat.icon}
                            </div>

                            <span className="admin-stat-arrow">
                                ↗
                            </span>

                        </div>


                        <div className="admin-stat-value">
                            {stat.value}
                        </div>


                        <div className="admin-stat-label">
                            {stat.label}
                        </div>


                        <div className="admin-stat-change">
                            {stat.change}
                        </div>

                    </Link>

                ))}

            </div>


            {/* =========================================
                CONTENT GRID
            ========================================= */}

            <div className="admin-dashboard-grid">


                {/* =====================================
                    RECENT ACTIVITY
                ===================================== */}

                <section className="admin-panel">

                    <div className="admin-panel-header">

                        <div>

                            <span>
                                ACTIVITY
                            </span>

                            <h3>
                                Recent Activity
                            </h3>

                        </div>

                        <Link to="/admin/activity">
                            View All
                        </Link>

                    </div>


                    <div className="admin-activity-list">

                        {activities.length > 0 ? (

                            activities.map((activity) => (

                                <div
                                    className="admin-activity"
                                    key={activity.id}
                                >

                                    <div className="admin-activity-icon">
                                        {activity.icon}
                                    </div>


                                    <div className="admin-activity-content">

                                        <strong>
                                            {activity.title}
                                        </strong>

                                        <p>
                                            {activity.description}
                                        </p>

                                        <small>
                                            {activity.time}
                                        </small>

                                    </div>

                                </div>

                            ))

                        ) : (

                            <div className="admin-empty-state">
                                No recent activity.
                            </div>

                        )}

                    </div>

                </section>


                {/* =====================================
                    QUICK ACTIONS
                ===================================== */}

                <section className="admin-panel">

                    <div className="admin-panel-header">

                        <div>

                            <span>
                                SHORTCUTS
                            </span>

                            <h3>
                                Quick Actions
                            </h3>

                        </div>

                    </div>


                    <div className="admin-quick-actions">

                        <Link to="/admin/news">

                            <span>
                                +
                            </span>

                            <div>

                                <strong>
                                    Publish News
                                </strong>

                                <small>
                                    Create a news article
                                </small>

                            </div>

                        </Link>


                        <Link to="/admin/projects">

                            <span>
                                +
                            </span>

                            <div>

                                <strong>
                                    Add Project
                                </strong>

                                <small>
                                    Add a new initiative
                                </small>

                            </div>

                        </Link>


                        <Link to="/admin/media">

                            <span>
                                +
                            </span>

                            <div>

                                <strong>
                                    Upload Media
                                </strong>

                                <small>
                                    Add images or files
                                </small>

                            </div>

                        </Link>


                        <Link to="/admin/settings">

                            <span>
                                ⚙
                            </span>

                            <div>

                                <strong>
                                    Site Settings
                                </strong>

                                <small>
                                    Manage website settings
                                </small>

                            </div>

                        </Link>

                    </div>

                </section>

            </div>


            {/* =========================================
                WEBSITE STATUS
            ========================================= */}

            <section className="admin-status-panel">

                <div className="admin-status-indicator">

                    <span
                        className={
                            status?.website === "online"
                                ? "online"
                                : "offline"
                        }
                    />

                    {status?.website === "online"
                        ? "Website is Online"
                        : "Website is in Maintenance Mode"
                    }

                </div>


                <div className="admin-status-items">

                    <div>

                        <span>
                            Last Updated
                        </span>

                        <strong>
                            {status?.last_updated
                                ? new Date(
                                    status.last_updated
                                ).toLocaleString()
                                : "No updates yet"
                            }
                        </strong>

                    </div>


                    <div>

                        <span>
                            Environment
                        </span>

                        <strong>
                            {status?.environment
                                ? status.environment
                                    .charAt(0)
                                    .toUpperCase() +
                                status.environment.slice(1)
                                : "Unknown"
                            }
                        </strong>

                    </div>


                    <div>

                        <span>
                            API Status
                        </span>

                        <strong>
                            {status?.api === "connected"
                                ? "Connected"
                                : "Unavailable"
                            }
                        </strong>

                    </div>

                </div>

            </section>

        </div>

    );
};

export default Dashboard;